//
// # Editor Context
//
// Shared classifier for the current editing context. Both the command
// palette and the keyboard help (Reference) panel use this to decide which
// commands / shortcuts are relevant right now, so the two surfaces stay in
// sync without duplicating context logic.
//
// A "scope" tags a command or shortcut category with the editing context it
// belongs to. `scopeMatchesContext` is the single place that decides whether
// a scope is relevant given the live editor state — keep all context rules
// here.
//

import type { Workspace } from "./workspace";
import type { ContentKind } from "EditorCore";

// Which editing context an action / shortcut belongs to.
export type ContextScope =
  | "global" // always relevant: File, Edit, View, Help, Tools
  | "schematic"
  | "symbol"
  | "layout"
  | "route"; // route mode (a sub-mode of layout editing)

// A snapshot of the live editing context, read from the active editor.
export interface EditorContext {
  readonly hasActiveEditor: boolean;
  readonly contentKind: ContentKind | null;
  readonly isRouteMode: boolean;
  readonly isLocked: boolean;
}

// Read the current editing context from the active editor. Returns an
// "empty" context when no editor is active.
export function getEditorContext(workspace: Workspace): EditorContext {
  const editor = workspace.getActiveEditor();
  if (!editor) {
    return {
      hasActiveEditor: false,
      contentKind: null,
      isRouteMode: false,
      isLocked: false,
    };
  }
  return {
    hasActiveEditor: true,
    contentKind: editor.content.contentKind,
    isRouteMode: editor.isRouteMode,
    isLocked: editor.isLocked,
  };
}

// True if an item tagged with `scopes` is relevant in `ctx`.
// Items with no scopes (or only "global") are always relevant.
//
// Note there is no "graphics" scope. GraphicsContent is the editor's empty
// fallback state (a stub with all editing ops disabled, never created by a
// user action), so it has no commands or shortcuts of its own. In a graphics
// context only "global" / unscoped items match; everything schematic / symbol
// / layout / route specific is filtered out. That is intentional — graphics
// is not a first-class editing mode.
export function scopeMatchesContext(
  scopes: readonly ContextScope[] | undefined,
  ctx: EditorContext
): boolean {
  if (!scopes || scopes.length === 0) return true;
  return scopes.some((scope) => {
    switch (scope) {
      case "global":
        return true;
      case "schematic":
        return ctx.contentKind === "schematic";
      case "symbol":
        return ctx.contentKind === "symbol";
      case "layout":
        // Route mode is a sub-mode of layout editing, so layout-scoped items
        // remain relevant while routing.
        return ctx.contentKind === "layout";
      case "route":
        return ctx.isRouteMode;
    }
  });
}
